import { FC, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Affix, Button, Col, Modal, Row, Spin, Tooltip } from 'antd';
import { useMethods } from '@lhb/hook';
import { beautifyTheLowHighObject, beautifyThePrice, isNotEmpty } from '@lhb/func';
import cs from 'classnames';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import V2Drawer, { V2DrawerHandles } from '@/common/components/Feedback/V2Drawer';
import V2Tabs from '@/common/components/Data/V2Tabs';
import V2Table from '@/common/components/Data/V2Table';
import V2Title from '@/common/components/Feedback/V2Title';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import V2BarChart from '@/common/components/Charts/V2BarChart';
import V2LineChart from '@/common/components/Charts/V2LineChart';
import V2LogRecord from '@/common/components/SpecialBusiness/V2LogRecord';
import ShopDetailDrawer from '@/views/expandstore/pages/shopDetail/Drawer';
import styles from './index.module.less';
import TrafficConversionFunnel from './components/TrafficConversionFunnel';
import DecisionReportExport from './components/Export';
import { getDecisionReportMetricGrid, getLineIntersectionPoints } from './helpers';
import { useDocument } from '@/common/components/config-v2';
import { v4 } from 'uuid'; // 用来生成不重复的key
import { useCreateShareContentLink } from '@/common/hook/useShareContent';
import {
  CHART_CONFIG,
  DECISION_SHARE_PERMISSION,
  PAYBACK_CHART_CONFIG,
} from './config';
import { getYmDecisionReportApprovalInfos, getYmDecisionReportBasicInfo, getYmDecisionReportCoreIndicator, getYmDecisionReportCustomerFunnel, getYmDecisionReportFinancialCalculation, getYmDecisionReportOverview, getYmDecisionReportRentAffordabilityOverview } from '@/common/api/expandStore/chancepoint';
import { getApprovalDetail } from '@/common/api/expandStore/approveworkbench';
import V2Message from '@/common/components/Others/V2Hint/V2Message';

const suffix = v4();

/** 分享链接有效期（天） */
const SHARE_FAILURE_DAYS = 7;

const ANCHOR_ITEMS = [
  { key: `overview_${suffix}`, label: '决策概览' },
  { key: `funnel_${suffix}`, label: '客流转化漏斗' },
  { key: `basic_${suffix}`, label: '基本信息' },
  { key: `finance_${suffix}`, label: '财务测算' },
  { key: `rent_${suffix}`, label: '租金与回本' },
];

const DecisionReportDrawer: FC<{
  open?: boolean;
  onClose?: () => void;
  detail?: any;
  shareParams?: any;
  /** 分享场景：不渲染抽屉壳，报告直接铺满页面 */
  shared?: boolean;
  /** 从审批详情查看时隐藏审批结论 */
  fromApproval?: boolean;
}> = ({ open, onClose, detail, shareParams, shared = false, fromApproval = false }) => {
  const { createShareContentLink } = useCreateShareContentLink();
  const drawerWrapper: MutableRefObject<V2DrawerHandles | null> = useRef(null);
  // 分享场景下报告自身的滚动容器（此时没有抽屉，container 不能取抽屉 body）
  const sharedBodyRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const storeDetailRef: MutableRefObject<any> = useRef(null);
  const similarTableRef: MutableRefObject<any> = useRef(null);
  const [container, setContainer] = useState<any>(null);
  const documentReal = useDocument(); // 兼容微应用项目和常规项目
  const [activeTab, setActiveTab] = useState<string>(`overview_${suffix}`);
  const [overviewData, setOverviewData] = useState<any>(null);
  const [basicInfoData, setBasicInfoData] = useState<any>(null);
  const [coreIndicatorData, setCoreIndicatorData] = useState<any>(null);
  const [rentAffordabilityData, setRentAffordabilityData] = useState<any>(null);
  const [customerFunnelData, setCustomerFunnelData] = useState<any>(null);
  const [financialCalculationData, setFinancialCalculationData] = useState<any>(null);
  const [approvalInfos, setApprovalInfos] = useState<any[]>([]);
  const [approvalDetailVisible, setApprovalDetailVisible] = useState(false);
  const [approvalDetailData, setApprovalDetailData] = useState<any>(null);
  const [approvalDetailLoading, setApprovalDetailLoading] = useState(false);
  // const [tabAffixed, setTabAffixed] = useState<boolean>(false);
  // 分享入口
  const hasShareEntry = useMemo(() => {
    const { permissions } = overviewData || {};
    return permissions?.some?.((item: any) => item?.event === DECISION_SHARE_PERMISSION);
  }, [overviewData]);
  const reportName = detail?.name || '';
  // 报告接口入参统一取 chancePointId：抽屉场景取 detail.id，分享场景取 shareParams.chancePointId
  const chancePointId = shareParams?.chancePointId || detail?.id;
  const methods = useMethods({
    loadOverview() {
      if (!chancePointId) return;
      getYmDecisionReportOverview({
        chancePointId,
      }).then((res: any) => {
        setOverviewData(res || null);
      }, (err: any) => {
        console.log(err);
        setOverviewData(null);
      });
    },
    loadApprovalInfos() {
      if (!chancePointId) return;
      getYmDecisionReportApprovalInfos({
        chancePointId,
      }).then((res: any) => {
        setApprovalInfos(Array.isArray(res) ? res : []);
      }, (err: any) => {
        console.log(err);
        setApprovalInfos([]);
      });
    },
    loadBasicInfo() {
      if (!chancePointId) return;
      getYmDecisionReportBasicInfo({
        chancePointId,
      }).then((res: any) => {
        setBasicInfoData(res || null);
      }, (err: any) => {
        console.log(err);
        setBasicInfoData(null);
      });
    },
    loadCoreIndicator() {
      if (!chancePointId) return;
      getYmDecisionReportCoreIndicator({
        chancePointId,
      }).then((res: any) => {
        setCoreIndicatorData(res || null);
      }, (err: any) => {
        console.log(err);
        setCoreIndicatorData(null);
      });
    },
    loadRentAffordabilityOverview() {
      if (!chancePointId) return;
      getYmDecisionReportRentAffordabilityOverview({
        chancePointId,
      }).then((res: any) => {
        setRentAffordabilityData(res || null);
      }, (err: any) => {
        console.log(err);
        setRentAffordabilityData(null);
      });
    },
    loadCustomerFunnel() {
      if (!chancePointId) return;
      getYmDecisionReportCustomerFunnel({
        chancePointId,
      }).then((res: any) => {
        setCustomerFunnelData(res || null);
      }, (err: any) => {
        console.log(err);
        setCustomerFunnelData(null);
      });
    },
    loadFinancialCalculation() {
      if (!chancePointId) return;
      getYmDecisionReportFinancialCalculation({
        chancePointId,
      }).then((res: any) => {
        setFinancialCalculationData(res || null);
      }, (err: any) => {
        console.log(err);
        setFinancialCalculationData(null);
      });
    },
    getApprovalInfo(index: number) {
      const isModelApproval = index === 0;
      return approvalInfos.find((item) => {
        const approvalType = item?.approvalType;
        if (approvalType === undefined || approvalType === null || approvalType === '') {
          return false;
        }
        return isModelApproval ? String(approvalType) === '1' : String(approvalType) !== '1';
      });
    },
    loadApprovalDetail(index: number) {
      const approvalInfo = methods.getApprovalInfo(index);
      setApprovalDetailData(null);
      setApprovalDetailVisible(true);
      if (!approvalInfo?.approvalId) {
        setApprovalDetailLoading(false);
        return;
      }
      setApprovalDetailLoading(true);
      getApprovalDetail({
        id: approvalInfo.approvalId,
      }).then((res: any) => {
        setApprovalDetailData(res || null);
      }, (err: any) => {
        console.log(err);
        setApprovalDetailData(null);
      }).finally(() => {
        setApprovalDetailLoading(false);
      });
    },
    getApprovalLogItems() {
      if (!approvalDetailData) {
        return [];
      }
      const items: any[] = [];
      if (approvalDetailData?.creator || approvalDetailData?.createdAt) {
        items.push({
          name: '发起审批',
          time: approvalDetailData.createdAt,
          status: 'finish',
          description: `发起人：${methods.getHeroValue(approvalDetailData.creator)}；审批理由：${methods.getHeroValue(approvalDetailData.applyReason || approvalDetailData.remark)}`,
        });
      }
      if (Array.isArray(approvalDetailData.nodes)) {
        approvalDetailData.nodes.forEach((node: any, nodeIndex: number) => {
          const operators = Array.isArray(node?.operators) ? node.operators : [];
          const operatorDescription = operators.length ? operators.map((operator: any) => `${methods.getHeroValue(operator?.employeeName)} · ${methods.getHeroValue(operator?.statusName)}${operator?.reason ? `：${operator.reason}` : ''}`).join('；') : '-';
          items.push({
            name: node?.name || `审批节点${nodeIndex + 1}`,
            titleExtra: node?.statusName,
            time: operators.find((operator: any) => operator?.operateAt)?.operateAt,
            status: 'finish',
            description: operatorDescription,
          });
        });
      }
      return items;
    },
    openModelApproval() {
      methods.loadApprovalDetail(0);
    },
    openStoreApproval() {
      methods.loadApprovalDetail(1);
    },
    closeApprovalDetail() {
      setApprovalDetailVisible(false);
      setApprovalDetailData(null);
      setApprovalDetailLoading(false);
    },
    renderHeroDescription() {
      const data = overviewData || {};
      const recommendStoreFormatConclusion = methods.getHeroValue(data.recommendStoreFormatConclusion);
      const hasFormatMismatch = String(recommendStoreFormatConclusion).includes('不一致');
      return (
        <>
          本点位位于<span>{methods.getHeroValue(data.contactaddress)}</span>，商圈类型为<span>{methods.getHeroValue(data.businessDistrictType)}</span>，选址等级<span>{methods.getHeroValue(data.siteTier)}</span>，业务模式<span>{methods.getHeroValue(data.ymYWMS)}</span>。现场踏勘早高峰客流<span>{methods.getHeroValue(data.ymZGFKL)}</span>人，晚高峰客流<span>{methods.getHeroValue(data.ymWGFKL)}</span>人。全年经营数据显示年月日均业绩 <span>¥{methods.getHeroValue(data.ymNYRJYJ)}/天</span>、年月日均进店客流 <span>{methods.getHeroValue(data.ymNYRJJDKL)} 人/天</span>、年月日均捕获率 <span>{methods.getHeroValue(data.ymNYRJBHL)}%</span>、回本周期 <span>{methods.getHeroValue(data.paybackPeriod)} 个月</span>，{methods.getHeroValue(data.paybackPeriodEvaluation)}。推荐门店性质为<span>{methods.getHeroValue(data.ymTJMDXZ)}</span>，<span style={{ color: hasFormatMismatch ? '#FFC07D' : undefined }}>{recommendStoreFormatConclusion}</span>。
        </>
      );
    },
    getHeroValue(value: any) {
      return value === undefined || value === null || value === '' ? '-' : value;
    },
    handleScroll() {
      for (let i = 0; i < ANCHOR_ITEMS.length; i++) {
        const content = documentReal.querySelector(`.${ANCHOR_ITEMS[i].key}`);
        // 获取 tab 内容的位置信息
        const { top } = content!.getBoundingClientRect();
        const firstTabsOffsetTop = documentReal.querySelector(`.${ANCHOR_ITEMS[0].key}`)!.getBoundingClientRect().top;
        if (top <= 144 && top > 0) {
          // 如果tab的顶部距离页面顶部的距离小于等于80，就说明该tab已经进入可视区域，此时就可以设置该tab为选中状态，80为title距顶部距离70+10是增大选中范围，可自设
          setActiveTab(ANCHOR_ITEMS[i].key); // 设置选中的 tab
          break;
        } else if (container!.scrollTop <= firstTabsOffsetTop) {
          // 一般tabs上会有title部分，如果快速滚动到顶部，设置第一个tab为选中状态
          setActiveTab(ANCHOR_ITEMS[0].key);
          break;
        } else if (container!.scrollTop + container!.clientHeight >= container!.scrollHeight) {
          // 如果快速滚动到底部，确保设置最后一个tab为选中状态
          setActiveTab(ANCHOR_ITEMS[ANCHOR_ITEMS.length - 1].key);
          break;
        }
      };
    },
    scrollToAnchor(key: string) {
      setActiveTab(key);
      const element: HTMLElement = documentReal.querySelector(`.${key}`)!;
      container && element && container.scrollTo({
        top: element.offsetTop - 134, // 70为title距顶部距离，demo中为tabs高度 55.16 + 16 标题距顶部距离,因为tabs高度不为整数，多滚1px可以显示出按钮
        left: 0,
        behavior: 'smooth'
      });
    },
    async loadSimilarData() {
      const similarStores = Array.isArray(coreIndicatorData?.similarStores) ? coreIndicatorData.similarStores : [];
      const dataSource = similarStores.map((store: any) => ({
        ...store,
        similarity: typeof store.similarity === 'number' ? `${store.similarity}%` : store.similarity,
        modelEstimateSales: typeof store.modelEstimateSales === 'number' ? `¥${beautifyThePrice(store.modelEstimateSales, ',', 'auto')}` : store.modelEstimateSales,
        brokerage: typeof store.brokerage === 'number' ? `${store.brokerage}%` : store.brokerage,
      }));
      return { dataSource, count: dataSource.length };
    },
    renderMetric(item: ReturnType<typeof getDecisionReportMetricGrid>[number]) {
      const trend = methods.getMetricTrend(item.key);
      const monthlyValues = Array.isArray(trend?.monthlyValues) ? trend.monthlyValues : [];
      const metricValue = methods.getMetricValueData(item.key, trend?.annualAverage);
      const hasLargeUnit = item.key === 'dailyEntryFlow' || item.key === 'dailyPerformance';
      return <div className={styles.metricCard} key={item.key}>
        <V2Title type='H3' text={item.title} bold style={{ marginBottom: '4px' }}/>
        <div className={styles.metricValueOuter}>
          <div className={styles.metricValue}>{hasLargeUnit ? <>{metricValue.value}{item.key === 'dailyEntryFlow' && <span className={styles.metricUnit}>{metricValue.unit}</span>}</> : methods.getMetricValue(item.key, trend?.annualAverage)}</div>
          <div className={styles.metricDetail}>{methods.getMetricDetail(item.key, trend)}</div>
        </div>
        <V2BarChart
          xAxisData={monthlyValues.map((monthlyValue: any) => monthlyValue?.month || '-')}
          seriesData={[{ name: item.title, data: monthlyValues.map((monthlyValue: any) => monthlyValue?.value), itemStyle: { color: item.color, borderRadius: [4, 4, 0, 0] }, barMaxWidth: 16 }]}
          height={164}
          resizable
          config={CHART_CONFIG}
        />
      </div>;
    },
    getPaybackChartData() {
      const chart = coreIndicatorData?.paybackPeriodChart;
      const monthlyData = Array.isArray(chart?.monthlyData) ? chart.monthlyData : [];
      const rows = monthlyData.filter((item: any) => (
        typeof item?.cumulativeCashFlow === 'number' &&
        typeof item?.initialInvestment === 'number'
      ));
      if (!rows.length) {
        return { xAxisData: [], cumulativeCashFlow: [], initialInvestment: [], intersections: [] };
      }
      const cumulativeCashFlow = rows.map((item: any) => item.cumulativeCashFlow);
      const initialInvestment = rows.map((item: any) => item.initialInvestment);
      return {
        xAxisData: rows.map((item: any, index: number) => `${item.month || index + 1}月`),
        cumulativeCashFlow,
        initialInvestment,
        intersections: getLineIntersectionPoints(cumulativeCashFlow, initialInvestment),
      };
    },
    getPaybackValue(value: any) {
      if (value === undefined || value === null || value === '') return '-';
      return typeof value === 'number' ? beautifyThePrice(value, ',', 'auto') : value;
    },
    getPaybackRevenueValue(value: any) {
      if (value === undefined || value === null || value === '') return '-';
      if (typeof value !== 'number') return value;
      const formattedValue = beautifyTheLowHighObject(2, value, 2, { low: '元', high: '万元', top: '亿元' });
      return `¥${beautifyThePrice(formattedValue.val, ',', 'auto')}${formattedValue.unit}`;
    },
    renderPaybackMetric() {
      const chart = coreIndicatorData?.paybackPeriodChart || {};
      const chartData = methods.getPaybackChartData();
      const isUnableToPayback = Number(chart.paybackPeriod) < 0;
      const detailItems = [
        methods.hasFinancialValue(chart.initialInvestment) ? `前期投入 ¥${methods.getPaybackValue(chart.initialInvestment)}万` : '',
        methods.hasFinancialValue(chart.annualCashFlow) ? `年现金流 ¥${methods.getPaybackValue(chart.annualCashFlow)}万 ×12` : '',
        methods.hasFinancialValue(chart.annualRevenue) ? `年营业额 ${methods.getPaybackRevenueValue(chart.annualRevenue)}` : '',
      ].filter(Boolean);
      return (
        <div className={cs(styles.metricCard, styles.paybackMetricCard)} key='paybackPeriod'>
          <V2Title type='H3' text='回本周期' bold style={{ marginBottom: '4px' }}/>
          <div className={styles.metricValueOuter}>
            <div className={styles.metricValue} style={isUnableToPayback ? { color: '#F53F3F' } : undefined}>{methods.hasFinancialValue(chart.paybackPeriod) ? (isUnableToPayback ? '无法回本' : <>{methods.getPaybackValue(chart.paybackPeriod)}<span className={styles.metricUnit}>个月</span></>) : '-'}</div>
            <div className={styles.metricDetail}>{detailItems.length ? detailItems.join(' · ') : '-'}</div>
          </div>
          {chartData.xAxisData.length > 0 && <V2LineChart
            xAxisData={chartData.xAxisData}
            seriesData={[
              {
                name: '累计现金流',
                data: chartData.cumulativeCashFlow.map((value: number, index: number) => [index, value]),
                symbol: 'none',
                lineStyle: { color: '#6268D9', width: 3 },
                itemStyle: { color: '#6268D9' },
              },
              {
                name: '前期投入',
                data: chartData.initialInvestment.map((value: number, index: number) => [index, value]),
                symbol: 'none',
                lineStyle: { color: '#FF9838', width: 3 },
                itemStyle: { color: '#FF9838' },
                markPoint: chartData.intersections.length ? {
                  symbol: 'circle',
                  symbolSize: 14,
                  label: { show: false },
                  itemStyle: { color: '#3CC4E7', borderColor: '#fff', borderWidth: 2 },
                  data: chartData.intersections,
                } : undefined,
              },
            ]}
            height={164}
            resizable
            config={PAYBACK_CHART_CONFIG}
          />}
        </div>
      );
    },
    getMetricTrend(key: string) {
      const trendKeyMap: Record<string, string> = {
        dailyCaptureRate: 'dailyCaptureRateTrend',
        dailyEntryFlow: 'dailyEntryFlowTrend',
        dailyPerformance: 'dailyPerformanceTrend',
      };
      return coreIndicatorData?.[trendKeyMap[key]];
    },
    getMetricValue(key: string, value: any) {
      const metricValue = methods.getMetricValueData(key, value);
      return `${metricValue.value}${metricValue.unit}`;
    },
    getMetricValueData(key: string, value: any) {
      if (value === undefined || value === null || value === '') return { value: '-', unit: '' };
      if (key === 'dailyEntryFlow' || key === 'dailyPerformance') {
        const unit = key === 'dailyEntryFlow'
          ? { low: '人', high: '万人', top: '亿人' }
          : { low: '', high: '', top: '' };
        const formattedValue = beautifyTheLowHighObject(1, value, 2, unit);
        const numberValue = beautifyThePrice(formattedValue.val, ',', 'auto');
        return { value: `${key === 'dailyPerformance' ? '¥' : ''}${numberValue}`, unit: formattedValue.unit };
      }
      const numberValue = beautifyThePrice(value, ',', 2);
      return { value: numberValue, unit: '%' };
    },
    getMetricDetail(key: string, trend: any) {
      const annualAverage = methods.getMetricValue(key, trend?.annualAverage);
      const peakValue = methods.getMetricValue(key, trend?.peakValue);
      const valleyValue = methods.getMetricValue(key, trend?.valleyValue);
      const peakMonth = methods.getHeroValue(trend?.peakMonth);
      const valleyMonth = methods.getHeroValue(trend?.valleyMonth);
      return `年均 ${annualAverage} · ${peakMonth}峰值 ${peakValue} · ${valleyMonth}谷值 ${valleyValue}`;
    },
    renderBasicItem(item: string[]) {
      return <Col span={8} key={item[0]}>
        <V2DetailItem
          label={item[0]}
          value={item[1]}
        />
      </Col>;
    },
    getBasicInfoValue(value: any, digits: number | 'auto' = 'auto') {
      if (value === undefined || value === null || value === '') return '-';
      return typeof value === 'number' ? beautifyThePrice(value, ',', digits) : value;
    },
    getBasicInfoItems() {
      const data = basicInfoData || {};
      return [
        ['店铺名称', methods.getBasicInfoValue(data.shopnameYM)],
        ['店铺地址', methods.getBasicInfoValue(data.contactaddress)],
        ['店铺面积', `${methods.getBasicInfoValue(data.basicUsableShopArea)} ㎡`],
        ['门头宽度', `${methods.getBasicInfoValue(data.storeWidth)} 米`],
        ['选址等级', methods.getBasicInfoValue(data.siteTier)],
        ['业务模式', methods.getBasicInfoValue(data.ymYWMS)],
        ['门店性质', methods.getBasicInfoValue(data.storeFormat)],
        ['推荐门店性质', methods.getBasicInfoValue(data.ymTJMDXZ)],
        ['早高峰客流', `${methods.getBasicInfoValue(data.ymZGFKL, 0)} 人`],
        ['晚高峰客流', `${methods.getBasicInfoValue(data.ymWGFKL, 0)} 人`],
        ['商圈评级', methods.getBasicInfoValue(data.retailDistrictRating)],
        ['商圈类型', methods.getBasicInfoValue(data.ymSQLX)],
      ];
    },
    renderFinanceRow(row: string[]) {
      return <div className={styles.financeItem} key={row[0]}>
        <span>{row[0]}</span>
        <strong>{row[1]}</strong>
        {row[2] && <small>{row[2]}</small>}
      </div>;
    },
    renderFinanceGroup(group: any) {
      return <div className={styles.financeGroup} key={group.number}>
        <div className={styles.financeGroupHeader}>
          <div className={styles.financeGroupHeading}>
            <div className={styles.financeGroupNumber}>{group.number}</div>
            <div className={styles.financeGroupTitle}>{group.title}</div>
            <div className={styles.financeGroupDescription}>{group.description}</div>
          </div>
          <div className={cs(styles.financeTotal, group.tone === 'orange' ? styles.financeTotalOrange : styles.financeTotalBlue)}>
            {group.total}
          </div>
        </div>
        <div className={cs(styles.financeGrid, group.columns === 5 ? styles.financeGridFive : styles.financeGridFour)}>{group.rows.map(methods.renderFinanceRow)}</div>
      </div>;
    },
    getFinancialValue(value: any, unit = '', digits: number | 'auto' = 'auto') {
      if (value === undefined || value === null || value === '') return '-';
      const numberValue = typeof value === 'number' ? beautifyThePrice(value, ',', digits) : value;
      return `${numberValue}${unit}`;
    },
    getFinancialCurrencyValue(value: any, unit = '') {
      if (value === undefined || value === null || value === '') return '-';
      return `¥${methods.getFinancialValue(value, unit)}`;
    },
    hasFinancialValue(value: any) {
      return value !== undefined && value !== null && value !== '';
    },
    getFinancialGroups() {
      const data = financialCalculationData || {};
      const income = data.income || {};
      const variableCost = data.variableCost || {};
      const fixedCost = data.fixedCost || {};
      const fund = data.fund || {};
      return [
        {
          number: '1', title: '收入情况', description: '基于历史门店模型与同商圈校正', total: methods.getFinancialValue(income.annualRevenue, ' 万元/年'), columns: 5, tone: 'blue',
          rows: [
            ['营业模式', methods.getFinancialValue(income.businessMode)], ['年实际营业天数', methods.getFinancialValue(income.actualOperatingDays, ' 天')], ['年营业收入', methods.getFinancialValue(income.annualRevenue, ' 万元')], ['年边际毛利率', methods.getFinancialValue(income.grossMarginRate, '%')], ['年边际毛利额', methods.getFinancialValue(income.grossMarginAmount, ' 万元')],
          ],
        },
        {
          number: '2', title: '变动成本', description: '年职工薪酬及社保公积金 · 年门店水电 · 年其他杂项', total: methods.getFinancialValue(variableCost.totalVariableCost, ' 万元/年'), columns: 4, tone: 'orange',
          rows: [
            ['应配员工数', methods.getFinancialValue(variableCost.requiredStaffCount, ' 人')], ['年职工薪酬及社保公积金', methods.getFinancialValue(variableCost.annualSalaryAndSocialSecurity, ' 万元')], ['年门店水电', methods.getFinancialValue(variableCost.annualWaterElectricity, ' 万元')], ['年其他杂项', methods.getFinancialValue(variableCost.annualOtherMisc, ' 万元')],
          ],
        },
        {
          number: '3', title: '固定成本', description: '装修摊销 · 设备折旧摊销 · 店铺租金 · 加盟费', total: methods.getFinancialValue(fixedCost.totalFixedCost, ' 万元/年'), columns: 4, tone: 'orange',
          rows: [
            ['装修总额', methods.getFinancialValue(fixedCost.decorationTotal, ' 万元')], ['年装修摊销', methods.getFinancialValue(fixedCost.annualDecorationAmortization, ' 万元')], ['设备总额', methods.getFinancialValue(fixedCost.equipmentTotal, ' 万元')], ['年设备折旧摊销', methods.getFinancialValue(fixedCost.annualEquipmentDepreciation, ' 万元')],
            ['年店铺租金', methods.getFinancialValue(fixedCost.annualShopRent, ' 万元')], ['年物业费', methods.getFinancialValue(fixedCost.annualPropertyFee, ' 万元')], ['转让费', methods.getFinancialValue(fixedCost.transferFee, ' 万元')], ['年转让费摊销', methods.getFinancialValue(fixedCost.annualTransferFeeAmortization, ' 万元')],
            ['年宿舍租金', methods.getFinancialValue(fixedCost.annualDormitoryRent, ' 万元')], ['加盟费', methods.getFinancialValue(fixedCost.franchiseFee, ' 万元')], ['年加盟费摊销', methods.getFinancialValue(fixedCost.annualFranchiseFeeAmortization, ' 万元')], ['培训费', methods.getFinancialValue(fixedCost.trainingFee, ' 万元')],
            ['年培训费摊销', methods.getFinancialValue(fixedCost.annualTrainingFeeAmortization, ' 万元')],
          ],
        },
        {
          number: '4', title: '资金情况', description: '', total: `回本 ${methods.getFinancialValue(fund.paybackPeriod, ' 个月')}`, columns: 4, tone: 'blue',
          rows: [
            ['年净利润', methods.getFinancialValue(fund.annualNetProfit, ' 万元')], ['年现金流', methods.getFinancialValue(fund.annualCashFlow, ' 万元')], ['前期投入', methods.getFinancialValue(fund.initialInvestment, ' 万元')], ['回本周期', methods.getFinancialValue(fund.paybackPeriod, ' 个月')],
          ],
        },
      ];
    },
    getFinancialConclusion() {
      const data = financialCalculationData?.recommendStoreFormat || {};
      const detailItems = [
        methods.hasFinancialValue(data.primeTradeZone) ? `本店为 ${data.primeTradeZone}` : '',
        methods.hasFinancialValue(data.annualAverageDailyPerformance) ? `年月日均业绩 ${methods.getFinancialCurrencyValue(data.annualAverageDailyPerformance, '/天')}` : '',
        methods.hasFinancialValue(data.annualNetProfit) ? `年净利润 ${methods.getFinancialValue(data.annualNetProfit, ' 万元/年')}` : '',
        methods.hasFinancialValue(data.storeFormat) ? `当前门店性质 ${data.storeFormat}` : '',
        methods.hasFinancialValue(data.lowProfitPrompt) ? data.lowProfitPrompt : '',
      ].filter(Boolean);
      return {
        recommendStoreFormat: data.recommendStoreFormat,
        consistencyPrompt: data.consistencyPrompt,
        detail: detailItems.length ? `${detailItems.join('，')}。` : '',
      };
    },
    renderRentItem(item: any) {
      return <div className={cs(styles.rentItem, item.isActual && styles.rentItemActual)} key={item.key}>
        <div className={styles.rentItemTitle}>{item.title}</div>
        <div className={styles.rentItemValue}>
          <div>{item.value || '-'}</div>
          <div className={styles.rentItemValueUnit}>{item.unit}</div>
          {item.isActual && <div className={styles.rentItemValueTag}>实际租金</div>}
        </div>
        {item.details.map((detail: string, index: number) => <div className={styles.rentItemOther} key={`${item.key}-${index}`}>{detail}</div>)}
      </div>;
    },
    renderRentSummary(item: string[]) {
      return (
        <div className={styles.rentSummaryItem} key={item[0]}>
          <div className={styles.rentSummaryItemLeft}>
            <div className={styles.rentSummaryItemLeftTitle}>{item[0]}</div>
            <div className={styles.rentSummaryItemLeftValue}>
              {item[1]}
              <span>{item[2]}</span>
            </div>
          </div>
          <div className={styles.rentSummaryItemRight}>
            <div>{item[3]}</div>
            <div>{item[4]}</div>
            <div>{item[5]}</div>
          </div>
        </div>
      );
    },
    getRentOverviewValue(value: any, unit: any, currency = false) {
      if (value === undefined || value === null || value === '') return '-';
      const formattedValue = typeof value === 'number' ? beautifyThePrice(value, ',', 'auto') : value;
      return `${currency ? '¥' : ''}${formattedValue}${unit || ''}`;
    },
    getRentTierItems() {
      const rentTiers = Array.isArray(rentAffordabilityData?.rentTiers) ? rentAffordabilityData.rentTiers : [];
      const tierNameMap: Record<string, string> = {
        TARGET: '争取目标',
        ACTUAL: '实际签约',
        NET_PROFIT_ZERO: '净利率红线',
      };
      const tierTypeMap: Record<number, string> = {
        1: 'TARGET',
        2: 'ACTUAL',
        3: 'NET_PROFIT_ZERO',
      };
      return rentTiers.slice().sort((firstTier: any, secondTier: any) => Number(firstTier?.tierId) - Number(secondTier?.tierId)).map((tier: any) => {
        const tierType = tierTypeMap[tier?.tierId];
        const paybackText = methods.getRentOverviewValue(tier.paybackPeriod, tier.paybackPeriodUnit);
        const distanceText = methods.getRentOverviewValue(tier.distanceToRedLine, tier.distanceToRedLineUnit, true);
        const paybackDetail = [
          tier.paybackPeriod !== undefined && tier.paybackPeriod !== null && tier.paybackPeriod !== '' ? `回本 ${paybackText}` : '',
          tier.distanceToRedLine !== undefined && tier.distanceToRedLine !== null && tier.distanceToRedLine !== '' ? `距红线 ${distanceText}` : '',
        ].filter(Boolean).join('，');
        const breakEvenPerformanceText = tier.breakEvenDailyPerformance !== undefined && tier.breakEvenDailyPerformance !== null && tier.breakEvenDailyPerformance !== '' ? `保本需日均业绩 ${methods.getRentOverviewValue(tier.breakEvenDailyPerformance, tier.breakEvenDailyPerformanceUnit, true)}` : '';
        const breakEvenEntryText = tier.breakEvenDailyEntryCount !== undefined && tier.breakEvenDailyEntryCount !== null && tier.breakEvenDailyEntryCount !== '' ? `日进店 ${methods.getRentOverviewValue(tier.breakEvenDailyEntryCount, tier.breakEvenDailyEntryCountUnit)}` : '';
        const breakEvenDetail = breakEvenPerformanceText && breakEvenEntryText
          ? `${breakEvenPerformanceText}（${breakEvenEntryText}）`
          : breakEvenPerformanceText || breakEvenEntryText;
        const costDetail = [
          tier.annualFixedCost !== undefined && tier.annualFixedCost !== null && tier.annualFixedCost !== '' ? `年固定成本 ${methods.getRentOverviewValue(tier.annualFixedCost, tier.annualFixedCostUnit, true)}` : '',
          tier.annualNetProfit !== undefined && tier.annualNetProfit !== null && tier.annualNetProfit !== '' ? `年净利 ${methods.getRentOverviewValue(tier.annualNetProfit, tier.annualNetProfitUnit, true)}` : '',
        ].filter(Boolean).join('｜');
        const tierExtraDetail = tierType === 'ACTUAL'
          ? paybackDetail
          : tierType === 'NET_PROFIT_ZERO'
            ? tier.warningMessage || ''
            : '';
        return {
          key: `${tier.tierId}-${tier.tierName || tierType || ''}`,
          title: tier.tierName || tierNameMap[tierType] || '',
          value: tier.rentAmount !== undefined && tier.rentAmount !== null && tier.rentAmount !== '' ? methods.getRentOverviewValue(tier.rentAmount, '') : '',
          unit: tier.rentAmountUnit || '',
          isActual: tierType === 'ACTUAL',
          details: [
            costDetail,
            breakEvenDetail,
            tierExtraDetail,
          ].filter(Boolean),
        };
      });
    },
    getRentSummaryItems() {
      const data = rentAffordabilityData || {};
      const profit = data.annualOperatingNetProfit || {};
      const payback = data.initialInvestmentPayback || {};
      const hasOverviewData = Object.keys(profit).length > 0 || Object.keys(payback).length > 0;
      if (!hasOverviewData) return [];
      return [
        [
          `年度运营净利（实际租金 ${methods.getRentOverviewValue(profit.rentAmountYM2, profit.rentAmountYM2Unit, true)}）`,
          methods.getRentOverviewValue(profit.ymNJLR, '', false),
          profit.ymNJLRUnit || '',
          `年边际毛利 ${methods.getRentOverviewValue(profit.ymNBJMLE, profit.ymNBJMLEUnit, true)}`,
          `年变动成本 ${methods.getRentOverviewValue(profit.ymNBDCB, profit.ymNBDCBUnit, true)}\n年固定成本 ${methods.getRentOverviewValue(profit.ymNGDCB, profit.ymNGDCBUnit, true)}`,
          `年净利率 ${methods.getRentOverviewValue(profit.ymNJLL, profit.ymNJLLUnit)}`,
        ],
        [
          '前期投入回本周期',
          methods.getRentOverviewValue(payback.ymHBZQ),
          payback.ymHBZQUnit || '',
          `前期投入 ${methods.getRentOverviewValue(payback.ymQQTR, payback.ymQQTRUnit, true)}\n年现金流 ${methods.getRentOverviewValue(payback.ymNXJL, payback.ymNXJLUnit, true)}`,
          `回本周期 ${methods.getRentOverviewValue(payback.ymHBZQ, payback.ymHBZQUnit)}`,
          methods.getRentOverviewValue(payback.paybackPeriodEvaluation),
        ],
      ];
    },
    hasFunnelValue(value: any) {
      return isNotEmpty(value) && !!+value;
    },
    getFunnelValue(value: any) {
      if (methods.hasFunnelValue(value)) return beautifyThePrice(+value, ',', 'auto');
      return '-';
    },
    // 空值时不带货币符号与单位，避免出现「¥-」「-人/天」这类拼接结果
    getFunnelUnit(value: any, unit: string) {
      return methods.hasFunnelValue(value) ? unit : '';
    },
    getFunnelCurrencyValue(value: any) {
      return methods.hasFunnelValue(value) ? `¥${methods.getFunnelValue(value)}` : '-';
    },
    getTrafficFunnelItems() {
      const data = customerFunnelData || {};
      // 接口已返回百分比数值，捕获率直接拼接 % 即可，不再乘以 100
      const captureRate = isNotEmpty(data?.ymNYRJBHL) ? `${data.ymNYRJBHL}%` : '-';
      return [
        {
          key: 'residencePopulation',
          label: '5分钟步行圈居住人口',
          subLabel: `常住人口 ${methods.getFunnelValue(data.ym5WFZCZRK)}${methods.getFunnelUnit(data.ym5WFZCZRK, '人')} | 工作人口 ${methods.getFunnelValue(data.ym5WFZGZRK)}${methods.getFunnelUnit(data.ym5WFZGZRK, '人')}`,
          value: methods.getFunnelValue(data.ym5WFZJZRK),
          unit: methods.getFunnelUnit(data.ym5WFZJZRK, '人'),
          width: '100%',
          color: '#60A6FF',
        },
        {
          key: 'dailyEntryFlow',
          label: '年月日均进店客流',
          subLabel: `年月日均捕获率 ${captureRate}`,
          value: methods.getFunnelValue(data.ymNYRJJDKL),
          unit: methods.getFunnelUnit(data.ymNYRJJDKL, '人/天'),
          width: '80%',
          color: '#42C7E8',
        },
        {
          key: 'dailyPerformance',
          label: '年月日均业绩',
          subLabel: `年月日均客单价 ${methods.getFunnelCurrencyValue(data.ymKDJ)}`,
          value: methods.getFunnelCurrencyValue(data.ymNYRJYJ),
          unit: methods.getFunnelUnit(data.ymNYRJYJ, '/天'),
          width: '58%',
          color: '#F59A3A',
        },
      ];
    },
    showStore(shopId: number) {
      storeDetailRef.current?.init?.(shopId);
    },
    reloadStore() {},
    async handleShare() {
      if (!chancePointId) return;
      try {
        const failureTime = dayjs().add(SHARE_FAILURE_DAYS, 'day').format('YYYY-MM-DD HH:mm:ss');
        const url = await createShareContentLink({ shareUrl: '/share/ymdecision', params: { chancePointId }, failureTime });
        if (!url) throw new Error('empty share link');
        const name = overviewData?.chancePointName || reportName;
        if (copy(name ? `${name}：${url}` : url)) V2Message.success('链接已复制');
        else V2Message.error('复制失败，请重试');
      } catch {
        V2Message.error('分享链接生成失败，请重试');
      }
    },
  });

  const defaultColumns = [
    { key: 'name', title: '门店名称', importWidth: true, width: 150,
      // 分享场景禁用门店详情入口，避免分享页弹出抽屉
      render: (_, item) => (shared
        ? <span>{item.name}</span>
        : <span className={styles.tableName} onClick={() => methods.showStore(item.storeId)}>{item.name}</span>)
    },
    { key: 'similarity', title: '相似度', importWidth: true, width: 'auto', render(v) {
      return <span className={styles.tableSimilarity}>{v}</span>;
    } },
    { key: 'storeSaleType', title: '门店性质', importWidth: true, width: 'auto' },
    { key: 'businessType', title: '商圈类型', importWidth: true, width: 90 },
    { key: 'businessLevel', title: '选址等级', importWidth: true, width: 'auto' },
    { key: 'area', title: '店铺面积', importWidth: true, width: 'auto' },
    { key: 'modelEstimateSales', title: '模型日均业绩', importWidth: true, width: 110 },
    { key: 'brokerage', title: '模型平均捕获率', importWidth: true, width: 130 },
    { key: 'openDate', title: '开业时间', importWidth: true, width: 'auto' },
    { key: 'floor', title: '门店等级', importWidth: true, width: 'auto' },
  ];

  useEffect(() => {
    if (!open) {
      setOverviewData(null);
      setBasicInfoData(null);
      setCoreIndicatorData(null);
      setRentAffordabilityData(null);
      setCustomerFunnelData(null);
      setFinancialCalculationData(null);
      setApprovalDetailData(null);
      setApprovalDetailLoading(false);
      return;
    }
    methods.loadOverview();
    if (!fromApproval) methods.loadApprovalInfos();
    methods.loadBasicInfo();
    methods.loadCoreIndicator();
    methods.loadRentAffordabilityOverview();
    methods.loadCustomerFunnel();
    methods.loadFinancialCalculation();
  }, [open, chancePointId, fromApproval]);

  useEffect(() => {
    if (open && coreIndicatorData) {
      similarTableRef.current?.onload?.();
    }
  }, [open, coreIndicatorData]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      // 分享场景没有抽屉，滚动容器指向报告自身的滚动区域
      const target = shared ? sharedBodyRef.current : drawerWrapper.current?.getBodyElement();
      target && setContainer(target);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open, shared]);

  useEffect(() => {
    container?.addEventListener('scroll', methods.handleScroll); // 给容器绑定滚动事件
    return () => {
      container?.removeEventListener('scroll', methods.handleScroll);
    };
  }, [container]);

  // 抽屉与分享页共用同一份报告内容，分享场景不用抽屉壳，直接铺满页面
  // eslint-disable-next-line indent
  const reportContent = <>
    <Affix target={() => container} offsetTop={0} style={{ height: '64px' }}>
      <div>
        <header className={styles.reportHeader}>
          <div>
            <div className={styles.mainTit}>
              {shared
                ? (overviewData?.chancePointName || '一鸣真鲜奶吧-决策报告')
                : <>一鸣真鲜奶吧{reportName && <>｜{reportName}</>}</>}
            </div>
            <div>{overviewData?.businessDistrictType && <div className={styles.typeText}>{overviewData.businessDistrictType}</div>}</div>
          </div>
          {
            !shared && <div className={styles.reportActions}>
              <DecisionReportExport open={!!open} detail={detail} permissions={overviewData?.permissions} />
              {hasShareEntry && <Button onClick={methods.handleShare}>分享</Button>}
            </div>
          }
        </header>
        <div className={cs(styles.initialTabs, `my_decisions_repeat_tabs`)}>
          <V2Tabs items={ANCHOR_ITEMS} onChange={methods.scrollToAnchor} activeKey={activeTab} />
        </div>
      </div>
    </Affix>
    <div className={styles.reportCanvasOuter}>
      <div className={styles.reportCanvas}>
        <section className={styles.overviewSection}>
          <div className={cs([styles.reportHero, ANCHOR_ITEMS[0].key])} style={{ height: fromApproval ? 'auto' : undefined, paddingBottom: fromApproval ? 24 : undefined, backgroundImage: `url(${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/chancepoint/decision_ym.png?t=2026082801)` }}>
            <div className={styles.reportHeroInner}>
              <div className={styles.heroTitle}>
                <div className={styles.heroTitleDot}>•</div>
                <div>决策评分 · 点位综合评估</div>
              </div>
              <div className={styles.heroMain}>
                <div>{overviewData?.shopnameYM || reportName}</div>
                {overviewData?.basicUsableShopArea !== undefined && overviewData?.basicUsableShopArea !== null && <div className={styles.reportNameSmall}>({overviewData.basicUsableShopArea}㎡)</div>}
                {overviewData?.siteTier && <div className={styles.heroScore}>{overviewData.siteTier}</div>}
              </div>
              <div className={styles.heroDes}>
                {methods.renderHeroDescription()}
              </div>
              {!fromApproval && <>
                <div className={styles.heroInsight}>
                  <div className={styles.heroInsightTitle}>
                    <div className={styles.heroInsightTitleDot}>•</div>
                    <div>{methods.getApprovalInfo(0)?.approvalTypeName || '模型问题 · 商圈客群'}</div>
                  </div>
                  <Tooltip title={<><span>选址理由：</span>{methods.getHeroValue(methods.getApprovalInfo(0)?.applyReason)} <span>群体结论：</span>{methods.getHeroValue(methods.getApprovalInfo(0)?.summary)}</>}>
                    <div className={styles.heroInsightText}>
                      <span>选址理由：</span>{methods.getHeroValue(methods.getApprovalInfo(0)?.applyReason)} <span>群体结论：</span>{methods.getHeroValue(methods.getApprovalInfo(0)?.summary)}
                    </div>
                  </Tooltip>
                  <div className={styles.heroInsightBtn} onClick={methods.openModelApproval}>查看详情</div>
                </div>
                <div className={styles.heroInsight} style={{ marginTop: '24px' }}>
                  <div className={styles.heroInsightTitle}>
                    <div className={styles.heroInsightTitleDot}>•</div>
                    <div>{methods.getApprovalInfo(1)?.approvalTypeName || '店铺问题 · 点位评估'}</div>
                  </div>
                  <Tooltip title={<><span>选址理由：</span>{methods.getHeroValue(methods.getApprovalInfo(1)?.applyReason)} <span>群体结论：</span>{methods.getHeroValue(methods.getApprovalInfo(1)?.summary)}</>}>
                    <div className={styles.heroInsightText}>
                      <span>选址理由：</span>{methods.getHeroValue(methods.getApprovalInfo(1)?.applyReason)} <span>群体结论：</span>{methods.getHeroValue(methods.getApprovalInfo(1)?.summary)}
                    </div>
                  </Tooltip>
                  <div className={styles.heroInsightBtn} onClick={methods.openStoreApproval}>查看详情</div>
                </div>
              </>}
            </div>
          </div>
          <section className={styles.similarSection}>
            <V2Title type='H2' text='相似门店' bold style={{ marginBottom: '13px' }} />
            <V2Table
              ref={similarTableRef}
              rowKey='storeId'
              defaultColumns={defaultColumns}
              onFetch={methods.loadSimilarData}
              pagination={false}
              emptyRender
              bordered={false}
              hideColumnPlaceholder
              floorWidth={80}
              className={styles.customTable}
            />
          </section>
          <div className={styles.metricGrid}>{getDecisionReportMetricGrid().map(methods.renderMetric)}{methods.renderPaybackMetric()}</div>
        </section>
        <section className={cs(styles.reportSection, styles.funnelSection)}>
          <V2Title type='H2' text='客流转化漏斗' className={ANCHOR_ITEMS[1].key} bold/>
          <TrafficConversionFunnel data={methods.getTrafficFunnelItems()} />
        </section>
        <section className={cs(styles.reportSection, styles.basicInfoSection)}>
          <V2Title type='H2' text='基本信息' className={ANCHOR_ITEMS[2].key} bold/>
          <Row gutter={[14, 0]}>{methods.getBasicInfoItems().map(methods.renderBasicItem)}</Row>
        </section>
        <section className={styles.reportSection}>
          <V2Title type='H2' text='门店财务测算模型' className={ANCHOR_ITEMS[3].key} bold />
          <div className={styles.financeContent}>{methods.getFinancialGroups().map(methods.renderFinanceGroup)}</div>
          <div className={styles.financeConclusion}>
            {methods.hasFinancialValue(methods.getFinancialConclusion().recommendStoreFormat) && <span className={styles.financeConclusionCon}>推荐门店性质：{methods.getFinancialConclusion().recommendStoreFormat}</span>}
            {methods.hasFinancialValue(methods.getFinancialConclusion().consistencyPrompt) && <span className={styles.financeConclusionPrompt}>{methods.getFinancialConclusion().consistencyPrompt}。</span>}
            {methods.getFinancialConclusion().detail && <span>{methods.getFinancialConclusion().detail}</span>}
          </div>
        </section>
        <section className={styles.reportSection}>
          <V2Title type='H2' text='租金与承受力测算' className={ANCHOR_ITEMS[4].key} bold/>
          <div className={styles.rentSummaryGrid}>{methods.getRentSummaryItems().map(methods.renderRentSummary)}</div>
          <div className={styles.rentGrid}>{methods.getRentTierItems().map(methods.renderRentItem)}</div>
        </section>
      </div>
      <div className={styles.reportCanvasBottom}>
        零售选址决策报告框架 ｜ 核心切入点 · 三条决策证据 · 区域增长率（适用于所有零售业态）
      </div>
    </div>
    {!fromApproval && <Modal
      title='审批进度详情'
      open={approvalDetailVisible}
      onCancel={methods.closeApprovalDetail}
      footer={null}
      width={760}
      destroyOnClose
    >
      {approvalDetailLoading ? <div className={styles.approvalEmpty}><Spin tip='审批详情加载中' /></div> : approvalDetailData ? <V2LogRecord items={methods.getApprovalLogItems()} /> : <div className={styles.approvalEmpty}>暂无审批信息</div>}
    </Modal>}
    <ShopDetailDrawer ref={storeDetailRef} reload={methods.reloadStore} readonly />
  </>;

  return shared ? (
    <div className={styles.sharedReport}>
      <div ref={sharedBodyRef} className={styles.sharedReportBody}>{reportContent}</div>
    </div>
  ) : (
    <V2Drawer
      open={open}
      onClose={onClose}
      destroyOnClose
      bodyStyle={{ padding: 0 }}
      contentWrapperStyle={{ width: '1100px' }}
      onRef={drawerWrapper}
      className={styles.customDrawer}
    >
      {reportContent}
    </V2Drawer>
  );
};

export default DecisionReportDrawer;
